// AnalyticsPage — Performance Analytics (P&L over time, win/loss, risk metrics)
// Layout:
//   - Top nav: < Back | "Analytics"
//   - Hero stat: Net P&L + change vs prev period
//   - Time range pills: 1D / 7D / 1M / 3M / 6M / 1Y / All
//   - Big P&L chart
//   - Performance grid: Win rate, Avg win, Avg loss, Profit factor, Sharpe, Max DD
//   - By pair breakdown
//   - By strategy breakdown
//   - Best/worst trades

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserAvatar } from '../../components/Brand';

const RANGES = ['1D', '7D', '1M', '3M', '6M', '1Y', 'All'] as const;
type RangeKey = typeof RANGES[number];

const PAIR_STATS = [
  { pair: 'ES', trades: 28, winRate: 0.71, pnl: 1240.50, pct: 12.4 },
  { pair: 'NQ', trades: 42, winRate: 0.66, pnl: 980.00, pct: 9.8 },
  { pair: 'CL', trades: 15, winRate: 0.53, pnl: 240.00, pct: 2.4 },
  { pair: 'GC', trades: 8,  winRate: 0.50, pnl: 180.00, pct: 1.8 },
  { pair: 'YM', trades: 12, winRate: 0.58, pnl: 120.00, pct: 1.2 },
];

const STRATEGY_STATS = [
  { name: 'Breakout', trades: 87, winRate: 0.66, pnl: 2280.50, contribution: 78 },
  { name: 'Mean Reversion', trades: 18, winRate: 0.72, pnl: 480.00, contribution: 16 },
];

export function AnalyticsPage() {
  const nav = useNavigate();
  const [range, setRange] = useState<RangeKey>('1M');
  const [p, setP] = useState({ value: 2920.50, change: 12.4 });

  // Generate chart points
  const points = (() => {
    const n = 30;
    const out: number[] = [];
    let v = 10000;
    for (let i = 0; i < n; i++) {
      v += (Math.sin(i * 0.2) * 200) + (Math.random() - 0.4) * 350;
      out.push(v);
    }
    return out;
  })();
  const finalPct = ((points[points.length - 1] - points[0]) / points[0]) * 100;

  return (
    <div className="min-h-screen bg-tf-surface-1 pb-8">
      <div className="sticky top-0 z-10 flex h-11 items-center justify-between border-b border-tf-border ios26-navbar px-2 backdrop-blur-xl">
        <button onClick={() => nav(-1)} data-testid="back" className="flex h-9 w-9 items-center justify-center text-tf-blue haptic">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M15 5l-7 7 7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
        <div className="absolute left-1/2 -translate-x-1/2 text-[15px] font-semibold">Analytics</div>
        <button className="flex h-9 w-9 items-center justify-center text-tf-blue">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M12 4v12M5 12l7 7 7-7M4 20h16" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
      </div>

      {/* Hero — Net P&L */}
      <div className="mx-4 mt-4">
        <div className="ios26-hero text-center">
          <div className="text-[11px] uppercase tracking-wider text-tf-text-3">Net P&L · {range}</div>
          <div className="mt-1 dynamic-large text-tf-text-1 tabular-nums">${p.value.toLocaleString()}</div>
          <div className="mt-1 flex items-center justify-center gap-2">
            <span className={['rounded-full px-2 py-0.5 text-[12px] font-semibold', p.change >= 0 ? 'bg-[#34C759]/15 text-tf-profit' : 'bg-[#FF3B30]/15 text-tf-loss'].join(' ')}>
              {p.change >= 0 ? '+' : ''}{p.change.toFixed(2)}%
            </span>
            <span className="text-[12px] text-tf-text-3">vs previous {range}</span>
          </div>
        </div>
      </div>

      {/* Time range */}
      <div className="mx-4 mt-3 ios26-seg" data-testid="analytics-range">
        {RANGES.map((r) => (
          <button
            key={r}
            onClick={() => setRange(r)}
            className={r === range ? 'active' : ''}
            data-testid={`range-${r}`}
          >
            {r}
          </button>
        ))}
      </div>

      {/* Big chart */}
      <div className="mx-4 mt-3 ios26-card p-4">
        <svg viewBox="0 0 360 160" preserveAspectRatio="none" className="h-40 w-full">
          <defs>
            <linearGradient id="ana-fill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={finalPct >= 0 ? '#34C759' : '#FF3B30'} stopOpacity="0.18" />
              <stop offset="100%" stopColor={finalPct >= 0 ? '#34C759' : '#FF3B30'} stopOpacity="0" />
            </linearGradient>
          </defs>
          {(() => {
            const min = Math.min(...points), max = Math.max(...points) - min || 1;
            const path = points.map((p, i) => {
              const x = (i / (points.length - 1)) * 360;
              const y = 160 - ((p - min) / max) * 130 - 15;
              return `${i === 0 ? 'M' : 'L'}${x.toFixed(1)} ${y.toFixed(1)}`;
            }).join(' ');
            return (
              <>
                <path d={path + ' L360 160 L0 160 Z'} fill="url(#ana-fill)" />
                <path d={path} fill="none" stroke={finalPct >= 0 ? '#34C759' : '#FF3B30'} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
              </>
            );
          })()}
        </svg>
      </div>

      {/* Performance grid (iOS 26 iOS-style stat tiles) */}
      <h2 className="mt-5 px-4 pb-2 text-[13px] font-normal uppercase tracking-wide text-tf-text-3">Performance</h2>
      <div className="mx-4 grid grid-cols-2 gap-2">
        <MetricCard label="Win rate" value="67%" sub="80 of 119 trades" />
        <MetricCard label="Avg win" value="+$184" sub="vs avg loss -$98" />
        <MetricCard label="Avg loss" value="-$98" sub="Risk-reward 1.9" color="#FF3B30" />
        <MetricCard label="Profit factor" value="2.13" sub="Profits / losses" />
        <MetricCard label="Sharpe" value="1.92" sub="Risk-adj return" />
        <MetricCard label="Max drawdown" value="-8.4%" sub="Peak-to-trough" color="#FF3B30" />
        <MetricCard label="Avg hold" value="4.2h" sub="Per trade" />
        <MetricCard label="Trades/day" value="4.7" sub="Active days" />
      </div>

      {/* By pair */}
      <h2 className="mt-5 px-4 pb-2 text-[13px] font-normal uppercase tracking-wide text-tf-text-3">By instrument</h2>
      <div className="mx-4 ios26-list bg-white">
        {PAIR_STATS.map((p, i) => (
          <div key={p.pair} className={['flex items-center gap-3 px-4 py-3', i < PAIR_STATS.length - 1 ? 'border-b border-tf-border' : ''].join(' ')} data-testid={`pair-${p.pair}`}>
            <div className="flex h-9 w-9 items-center justify-center rounded-[10px] bg-tf-surface-1 text-[12px] font-bold text-tf-text-1">{p.pair.slice(0, 2)}</div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between">
                <span className="text-[14px] font-semibold text-tf-text-1">{p.pair}</span>
                <span className={['text-[14px] font-semibold tabular-nums', p.pnl >= 0 ? 'text-tf-profit' : 'text-tf-loss'].join(' ')}>
                  {p.pnl >= 0 ? '+' : ''}${p.pnl.toFixed(2)}
                </span>
              </div>
              <div className="mt-1 flex items-center gap-2">
                <div className="h-1 flex-1 overflow-hidden rounded-full bg-tf-surface-1">
                  <div className={['h-full rounded-full', p.pnl >= 0 ? 'bg-[#34C759]' : 'bg-[#FF3B30]'].join(' ')} style={{ width: p.pct + '%' }} />
                </div>
                <span className="text-[11px] tabular-nums text-tf-text-3">{p.trades} trades · {(p.winRate * 100).toFixed(0)}%</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* By strategy */}
      <h2 className="mt-5 px-4 pb-2 text-[13px] font-normal uppercase tracking-wide text-tf-text-3">By strategy</h2>
      <div className="mx-4 ios26-list bg-white">
        {STRATEGY_STATS.map((s, i) => (
          <div key={s.name} className={['flex items-center gap-3 px-4 py-3', i < STRATEGY_STATS.length - 1 ? 'border-b border-tf-border' : ''].join(' ')} data-testid={`strategy-stat-${s.name}`}>
            <div className="flex h-9 w-9 items-center justify-center rounded-[10px] text-[18px]" style={{ backgroundColor: '#34C75918' }}>🚀</div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between">
                <span className="text-[14px] font-semibold text-tf-text-1">{s.name}</span>
                <span className={['text-[14px] font-semibold tabular-nums', s.pnl >= 0 ? 'text-tf-profit' : 'text-tf-loss'].join(' ')}>
                  {s.pnl >= 0 ? '+' : ''}${s.pnl.toFixed(2)}
                </span>
              </div>
              <div className="text-[12px] text-tf-text-3">{s.trades} trades · {(s.winRate * 100).toFixed(0)}% win · {s.contribution}% of P&L</div>
            </div>
          </div>
        ))}
      </div>

      {/* Best / worst trades */}
      <h2 className="mt-5 px-4 pb-2 text-[13px] font-normal uppercase tracking-wide text-tf-text-3">Best & worst</h2>
      <div className="mx-4 grid grid-cols-2 gap-2">
        <div className="rounded-[16px] border border-[#34C759]/30 bg-[#34C759]/[0.04] p-3">
          <div className="text-[11px] uppercase tracking-wide text-tf-profit">Best trade</div>
          <div className="mt-1 text-[20px] font-bold tabular-nums text-tf-profit">+$420.00</div>
          <div className="text-[12px] text-tf-text-1">NQ long · 5/15 09:32</div>
        </div>
        <div className="rounded-[16px] border border-[#FF3B30]/30 bg-[#FF3B30]/[0.04] p-3">
          <div className="text-[11px] uppercase tracking-wide text-tf-loss">Worst trade</div>
          <div className="mt-1 text-[20px] font-bold tabular-nums text-tf-loss">-$185.00</div>
          <div className="text-[12px] text-tf-text-1">GC short · 5/12 14:55</div>
        </div>
      </div>

      {/* Streak */}
      <h2 className="mt-5 px-4 pb-2 text-[13px] font-normal uppercase tracking-wide text-tf-text-3">Streak</h2>
      <div className="mx-4 ios26-list bg-white">
        <div className="flex items-center justify-between px-4 py-3">
          <div>
            <div className="text-[14px] font-medium text-tf-text-1">Current win streak</div>
            <div className="text-[12px] text-tf-text-3">Best in last 30 days</div>
          </div>
          <div className="text-[24px] font-bold text-tf-profit">5 🔥</div>
        </div>
        <div className="flex items-center justify-between border-t border-tf-border px-4 py-3">
          <div>
            <div className="text-[14px] font-medium text-tf-text-1">Current loss streak</div>
            <div className="text-[12px] text-tf-text-3">Max allowed: 3</div>
          </div>
          <div className="text-[24px] font-bold text-tf-loss">0</div>
        </div>
      </div>
    </div>
  );
}

function MetricCard({ label, value, sub, color }: { label: string; value: string; sub: string; color?: string }) {
  return (
    <div className="rounded-[12px] border border-tf-border bg-white p-3">
      <div className="text-[11px] uppercase tracking-wide text-tf-text-3">{label}</div>
      <div className="mt-1 text-[20px] font-bold tabular-nums" style={{ color: color || '#1C1C1E' }}>{value}</div>
      <div className="text-[10px] text-tf-text-3">{sub}</div>
    </div>
  );
}
